import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as sppoAmendCreationAPI from '../../api/SpPOAPI/sppoAmendCreationAPI';
import { getPOUploadedDocs } from '../../api/SpPOAPI/spPoAmendAPI';

// Async Thunks
// =============================================

export const fetchVendorsForSPPOAmend = createAsyncThunk(
    'sppoAmendCreation/fetchVendorsForSPPOAmend',
    async (_, { rejectWithValue }) => {
        try { return await sppoAmendCreationAPI.getVendorsForSPPOAmend(); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Vendors'); }
    }
);

export const fetchCCForSPPOAmend = createAsyncThunk(
    'sppoAmendCreation/fetchCCForSPPOAmend',
    async (vendorCode, { rejectWithValue }) => {
        try { return await sppoAmendCreationAPI.getCCForSPPOAmendByVendor(vendorCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch Cost Centers'); }
    }
);

export const fetchPOForSPPOAmend = createAsyncThunk(
    'sppoAmendCreation/fetchPOForSPPOAmend',
    async ({ vendorCode, ccCode }, { rejectWithValue }) => {
        try { return await sppoAmendCreationAPI.getPOForSPPOAmend(vendorCode, ccCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch PO Numbers'); }
    }
);

export const fetchSPPOByNoForAmend = createAsyncThunk(
    'sppoAmendCreation/fetchSPPOByNoForAmend',
    async ({ sppono, ccCode, vendorCode }, { rejectWithValue }) => {
        try { return await sppoAmendCreationAPI.getSPPOByNoForAmend(sppono, ccCode, vendorCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch PO Details'); }
    }
);

export const fetchUploadDocSPPOAmendValue = createAsyncThunk(
    'sppoAmendCreation/fetchUploadDocSPPOAmendValue',
    async (ccCode, { rejectWithValue }) => {
        try { return await sppoAmendCreationAPI.getUploadDocSPPOAmendValue(ccCode); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch upload threshold'); }
    }
);

export const fetchSPPOAmendUploadedDocs = createAsyncThunk(
    'sppoAmendCreation/fetchSPPOAmendUploadedDocs',
    async ({ poNo, forType }, { rejectWithValue }) => {
        try { return await getPOUploadedDocs(poNo, forType); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to fetch uploaded documents'); }
    }
);

export const checkSPPOAmendDescriptions = createAsyncThunk(
    'sppoAmendCreation/checkSPPOAmendDescriptions',
    async (payload, { rejectWithValue }) => {
        try { return await sppoAmendCreationAPI.checkSPPOAmendDescriptions(payload); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to validate item descriptions'); }
    }
);

export const saveNewSPPOAmend = createAsyncThunk(
    'sppoAmendCreation/saveNewSPPOAmend',
    async (payload, { rejectWithValue }) => {
        try { return await sppoAmendCreationAPI.saveNewSPPOAmend(payload); }
        catch (error) { return rejectWithValue(error.Message || error.message || 'Failed to save SPPO Amend'); }
    }
);

// Initial State
// =============
const initialState = {
    vendors: [],
    costCenters: [],
    poNumbers: [],
    poDetails: null, // SPPO entity: { SPPOStartDate, SPPOEndDate, TotalValue, Balance, Remarks, ItemDescList }
    uploadValue: 0,
    uploadedDocs: [],

    saveResult: null,
    saveStatus: null, // null | 'pending' | 'success' | 'failed'
    checkStatus: null, // null | 'pending' | 'success' | 'failed'
    checkResult: null,

    loading: {
        vendors: false,
        costCenters: false,
        poNumbers: false,
        poDetails: false,
        uploadValue: false,
        uploadedDocs: false,
        check: false,
        save: false,
    },

    errors: {
        vendors: null,
        costCenters: null,
        poNumbers: null,
        poDetails: null,
        uploadValue: null,
        uploadedDocs: null,
        check: null,
        save: null,
    },
};

const sppoAmendCreationSlice = createSlice({
    name: 'sppoAmendCreation',
    initialState,
    reducers: {
        clearCostCenters: (state) => { state.costCenters = []; state.errors.costCenters = null; },
        clearPONumbers: (state) => { state.poNumbers = []; state.errors.poNumbers = null; },
        clearPODetails: (state) => { state.poDetails = null; state.errors.poDetails = null; },
        clearUploadedDocs: (state) => { state.uploadedDocs = []; state.errors.uploadedDocs = null; },
        clearCheckResult: (state) => { state.checkResult = null; state.checkStatus = null; state.errors.check = null; },
        clearSaveResult: (state) => { state.saveResult = null; state.saveStatus = null; state.errors.save = null; },
        resetSPPOAmendForm: (state) => {
            state.costCenters = []; state.poNumbers = []; state.poDetails = null;
            state.uploadValue = 0; state.uploadedDocs = [];
            state.saveResult = null; state.saveStatus = null;
            state.checkResult = null; state.checkStatus = null;
            state.errors = initialState.errors;
        },
        resetSPPOAmend: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchVendorsForSPPOAmend.pending, (state) => { state.loading.vendors = true; state.errors.vendors = null; })
            .addCase(fetchVendorsForSPPOAmend.fulfilled, (state, action) => { state.loading.vendors = false; state.vendors = action.payload?.Data || []; })
            .addCase(fetchVendorsForSPPOAmend.rejected, (state, action) => { state.loading.vendors = false; state.errors.vendors = action.payload; state.vendors = []; });

        builder
            .addCase(fetchCCForSPPOAmend.pending, (state) => { state.loading.costCenters = true; state.errors.costCenters = null; })
            .addCase(fetchCCForSPPOAmend.fulfilled, (state, action) => { state.loading.costCenters = false; state.costCenters = action.payload?.Data || []; })
            .addCase(fetchCCForSPPOAmend.rejected, (state, action) => { state.loading.costCenters = false; state.errors.costCenters = action.payload; state.costCenters = []; });

        builder
            .addCase(fetchPOForSPPOAmend.pending, (state) => { state.loading.poNumbers = true; state.errors.poNumbers = null; })
            .addCase(fetchPOForSPPOAmend.fulfilled, (state, action) => { state.loading.poNumbers = false; state.poNumbers = action.payload?.Data || []; })
            .addCase(fetchPOForSPPOAmend.rejected, (state, action) => { state.loading.poNumbers = false; state.errors.poNumbers = action.payload; state.poNumbers = []; });

        builder
            .addCase(fetchSPPOByNoForAmend.pending, (state) => { state.loading.poDetails = true; state.errors.poDetails = null; })
            .addCase(fetchSPPOByNoForAmend.fulfilled, (state, action) => { state.loading.poDetails = false; state.poDetails = action.payload?.Data || null; })
            .addCase(fetchSPPOByNoForAmend.rejected, (state, action) => { state.loading.poDetails = false; state.errors.poDetails = action.payload; state.poDetails = null; });

        builder
            .addCase(fetchUploadDocSPPOAmendValue.pending, (state) => { state.loading.uploadValue = true; state.errors.uploadValue = null; })
            .addCase(fetchUploadDocSPPOAmendValue.fulfilled, (state, action) => {
                state.loading.uploadValue = false;
                const data = action.payload?.Data || [];
                state.uploadValue = data.length > 0 ? (data[0].uploadvalue || 0) : 0;
            })
            .addCase(fetchUploadDocSPPOAmendValue.rejected, (state, action) => { state.loading.uploadValue = false; state.errors.uploadValue = action.payload; state.uploadValue = 0; });

        builder
            .addCase(fetchSPPOAmendUploadedDocs.pending, (state) => { state.loading.uploadedDocs = true; state.errors.uploadedDocs = null; })
            .addCase(fetchSPPOAmendUploadedDocs.fulfilled, (state, action) => { state.loading.uploadedDocs = false; state.uploadedDocs = action.payload?.Data || []; })
            .addCase(fetchSPPOAmendUploadedDocs.rejected, (state, action) => { state.loading.uploadedDocs = false; state.errors.uploadedDocs = action.payload; state.uploadedDocs = []; });

        builder
            .addCase(checkSPPOAmendDescriptions.pending, (state) => { state.loading.check = true; state.errors.check = null; state.checkStatus = 'pending'; })
            .addCase(checkSPPOAmendDescriptions.fulfilled, (state, action) => {
                state.loading.check = false;
                state.checkResult = action.payload;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                if (responseStr === 'NotExists') { state.checkStatus = 'success'; }
                else { state.checkStatus = 'failed'; state.errors.check = responseStr || 'Validation failed'; }
            })
            .addCase(checkSPPOAmendDescriptions.rejected, (state, action) => { state.loading.check = false; state.errors.check = action.payload; state.checkStatus = 'failed'; });

        builder
            .addCase(saveNewSPPOAmend.pending, (state) => { state.loading.save = true; state.errors.save = null; state.saveStatus = 'pending'; })
            .addCase(saveNewSPPOAmend.fulfilled, (state, action) => {
                state.loading.save = false;
                state.saveResult = action.payload;
                const responseStr = typeof action.payload?.Data === 'string' ? action.payload.Data : '';
                const [status] = responseStr.split('$');
                if (status === 'Submited') { state.saveStatus = 'success'; }
                else { state.saveStatus = 'failed'; state.errors.save = responseStr || 'Save failed'; }
            })
            .addCase(saveNewSPPOAmend.rejected, (state, action) => { state.loading.save = false; state.errors.save = action.payload; state.saveStatus = 'failed'; state.saveResult = null; });
    },
});

export const {
    clearCostCenters,
    clearPONumbers,
    clearPODetails,
    clearUploadedDocs,
    clearCheckResult,
    clearSaveResult,
    resetSPPOAmendForm,
    resetSPPOAmend,
} = sppoAmendCreationSlice.actions;

// Selectors
// =========
const arr = (x) => (Array.isArray(x) ? x : []);

export const selectVendorsArray = (state) => arr(state.sppoAmendCreation.vendors);
export const selectVendorsLoading = (state) => state.sppoAmendCreation.loading.vendors;

export const selectCostCentersArray = (state) => arr(state.sppoAmendCreation.costCenters);
export const selectCostCentersLoading = (state) => state.sppoAmendCreation.loading.costCenters;

export const selectPONumbersArray = (state) => arr(state.sppoAmendCreation.poNumbers);
export const selectPONumbersLoading = (state) => state.sppoAmendCreation.loading.poNumbers;

export const selectPODetails = (state) => state.sppoAmendCreation.poDetails;
export const selectPODetailsLoading = (state) => state.sppoAmendCreation.loading.poDetails;

export const selectUploadValue = (state) => state.sppoAmendCreation.uploadValue;

export const selectUploadedDocsArray = (state) => arr(state.sppoAmendCreation.uploadedDocs);
export const selectUploadedDocsLoading = (state) => state.sppoAmendCreation.loading.uploadedDocs;

export const selectCheckStatus = (state) => state.sppoAmendCreation.checkStatus;
export const selectCheckLoading = (state) => state.sppoAmendCreation.loading.check;
export const selectCheckError = (state) => state.sppoAmendCreation.errors.check;

export const selectSaveStatus = (state) => state.sppoAmendCreation.saveStatus;
export const selectSaveLoading = (state) => state.sppoAmendCreation.loading.save;
export const selectSaveError = (state) => state.sppoAmendCreation.errors.save;

export default sppoAmendCreationSlice.reducer;
